import { useState } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Briefcase, MapPin, Clock, Users, GraduationCap, ArrowRight, Upload, Send } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select } from '@/components/ui/select'
import { Card } from '@/components/ui/card'

const departments = [
  { value: '', label: 'All Departments' },
  { value: 'sales', label: 'Sales & Business Development' },
  { value: 'technology', label: 'Technology & Development' },
  { value: 'operations', label: 'Operations' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'finance', label: 'Finance & Accounts' },
]

const jobs = [
  {
    id: 1,
    title: 'Senior Business Development Manager',
    department: 'sales',
    location: 'Pune',
    type: 'Full-time',
    experience: '5-8 years',
    description: 'Lead business development initiatives for government and institutional clients across Maharashtra.',
    requirements: ['5+ years in B2B sales', 'Government tender experience', 'Strong negotiation skills', 'Travel flexibility'],
  },
  {
    id: 2,
    title: 'Full Stack Developer',
    department: 'technology',
    location: 'Pune',
    type: 'Full-time',
    experience: '3-5 years',
    description: 'Develop and maintain web applications for our digital transformation solutions.',
    requirements: ['React.js / Vue.js', 'Node.js / Java Spring Boot', 'PostgreSQL / MySQL', 'AWS / Azure experience'],
  },
  {
    id: 3,
    title: 'Project Manager - Infrastructure',
    department: 'operations',
    location: 'Mumbai',
    type: 'Full-time',
    experience: '7-10 years',
    description: 'Manage large-scale infrastructure projects for educational and healthcare institutions.',
    requirements: ['PMP certification', 'Infrastructure project experience', 'Vendor management', 'Budget management'],
  },
  {
    id: 4,
    title: 'Digital Marketing Executive',
    department: 'marketing',
    location: 'Pune',
    type: 'Full-time',
    experience: '2-4 years',
    description: 'Execute digital marketing campaigns and manage social media presence.',
    requirements: ['SEO/SEM expertise', 'Social media management', 'Content creation', 'Analytics tools'],
  },
  {
    id: 5,
    title: 'Account Executive',
    department: 'finance',
    location: 'Pune',
    type: 'Full-time',
    experience: '3-5 years',
    description: 'Handle accounts for multiple group companies and ensure compliance.',
    requirements: ['CA/CPA qualification', 'Tally/SAP experience', 'Tax knowledge', 'Audit coordination'],
  },
  {
    id: 6,
    title: 'Sales Executive - Education Sector',
    department: 'sales',
    location: 'Nagpur',
    type: 'Full-time',
    experience: '2-4 years',
    description: 'Drive sales of educational furniture and smart classroom solutions.',
    requirements: ['Sales experience in education', 'Relationship building', 'Territory management', 'Target oriented'],
  },
]

const benefits = [
  { icon: Briefcase, title: 'Career Growth', description: 'Clear path for advancement' },
  { icon: Users, title: 'Team Culture', description: 'Collaborative environment' },
  { icon: GraduationCap, title: 'Learning', description: 'Training & development programs' },
  { icon: Clock, title: 'Work-Life Balance', description: 'Flexible working hours' },
]

export default function Careers() {
  const [activeDepartment, setActiveDepartment] = useState('')
  const [showApply, setShowApply] = useState(false)
  const [selectedJob, setSelectedJob] = useState<typeof jobs[0] | null>(null)

  const filteredJobs = activeDepartment
    ? jobs.filter(j => j.department === activeDepartment)
    : jobs

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-900/50 to-transparent" />
        <div className="container-premium relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6">
              <Briefcase className="w-4 h-4 text-primary-400" />
              <span className="text-sm text-graphite-300">Join Our Team</span>
            </div>
            <h1 className="heading-xl text-white mb-6">
              Build Your <span className="gradient-text">Career</span> with Us
            </h1>
            <p className="text-xl text-graphite-300">
              Join a dynamic team working on transformative projects across India's
              government and institutional landscape.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 border-y border-white/5">
        <div className="container-premium">
          <div className="grid md:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card variant="glass" className="p-6 text-center">
                  <benefit.icon className="w-8 h-8 mx-auto mb-3 text-primary-400" />
                  <h3 className="text-white font-semibold mb-1">{benefit.title}</h3>
                  <p className="text-sm text-graphite-400">{benefit.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Job Listings */}
      <section className="section-padding">
        <div className="container-premium">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <h2 className="heading-md text-white">Open Positions</h2>
            <Select
              options={departments}
              value={activeDepartment}
              onChange={(e) => setActiveDepartment(e.target.value)}
              className="w-48"
            />
          </div>

          <div className="space-y-4">
            {filteredJobs.map((job, index) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card variant="hover" className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2">{job.title}</h3>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-graphite-400">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {job.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {job.type}
                        </span>
                        <span>{job.experience}</span>
                      </div>
                    </div>
                    <Button
                      variant="primary"
                      onClick={() => {
                        setSelectedJob(job)
                        setShowApply(true)
                      }}
                    >
                      Apply Now
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Apply Modal */}
      {showApply && selectedJob && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <Card variant="premium" className="p-8">
              <h2 className="heading-sm text-white mb-2">Apply for {selectedJob.title}</h2>
              <p className="text-graphite-400 mb-6">{selectedJob.location} • {selectedJob.type}</p>

              <form className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <Input label="Full Name *" placeholder="Your name" required />
                  <Input label="Email *" type="email" placeholder="your@email.com" required />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <Input label="Phone *" placeholder="+91 XXXXX XXXXX" required />
                  <Input label="Current Company" placeholder="Your current employer" />
                </div>
                <Textarea label="Cover Letter" placeholder="Tell us why you're a great fit..." rows={4} />

                <div className="border-2 border-dashed border-white/20 rounded-xl p-8 text-center">
                  <Upload className="w-8 h-8 mx-auto text-graphite-500 mb-2" />
                  <p className="text-graphite-400 mb-2">Upload your resume (PDF, DOC)</p>
                  <Button variant="ghost" size="sm">Choose File</Button>
                </div>

                <div className="flex gap-4">
                  <Button type="submit" variant="gold" className="flex-1">
                    Submit Application
                    <Send className="ml-2 w-4 h-4" />
                  </Button>
                  <Button variant="ghost" onClick={() => setShowApply(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </Card>
          </motion.div>
        </div>
      )}

      {/* CTA */}
      <section className="section-padding">
        <div className="container-premium">
          <Card variant="premium" className="p-12 text-center">
            <h2 className="heading-md text-white mb-4">Don't see a suitable position?</h2>
            <p className="text-graphite-400 mb-8 max-w-2xl mx-auto">
              We're always looking for talented individuals. Send us your resume and we'll
              reach out when a matching position opens.
            </p>
            <Button variant="outline" asChild>
              <Link to="/contact">
                Send General Application
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </Card>
        </div>
      </section>
    </div>
  )
}